'use strict';

/**
 * View-model builder for PipelineStatus graph nodes. Facts bind through each stage's `factMatch` using
 * **`workflow`** (lane), **`pipeline` + `stage`** (node within the lane), and **`service`** (producer).
 * All four dimensions are required on the graph definition; `factMatch.service` must be non-empty after trim or `buildPipelineStatusGraphViewModel` throws.
 * Fact rows must carry the same four dimensions (non-empty after trim) to participate in indexing; rows missing any piece are ignored (fail-closed).
 * `description` is never used for identity. Latest row per bind key is deterministic (`occurredAt`, then lexicographic workflow+pipeline+stage+status, then `id`).
 *
 * **Display:** `workflow`, `scope`, and the card producer label `service` always come from the **graph definition** (`factMatch` + top-level `service` on each stage), not from matched fact rows. Facts supply status, timestamps, and other fact-only metadata.
 *
 * **Iteration manifests:** optional `iterationManifests` + `projectId` enable `iterationManifestMatch` + `completionPolicy: allIterationsSucceeded`.
 * The parent stage’s latest fact supplies `correlationId`; manifests are matched on that correlation plus workflow/pipeline/parentStage/iterationDimension.
 * Manifest-backed stages expose derived `iterationCompletedCount` / `iterationTotalCount` for UI (e.g. “2 / 5 completed”).
 * A manifest alone does not start the node: with no facts for its own iterations the stage stays pending.
 */

import { omitPathIllegalStarts } from './pipelineDuration';

/** @param {string | undefined | null} s */
const norm = (s) => (s == null ? '' : String(s).trim().toLowerCase());

/**
 * Trim-only identity for `factMatch` / fact fields (matches persisted casing from producers).
 * @param {string | undefined | null} s
 */
function trimPart(s) {
	return s == null ? '' : String(s).trim();
}

/**
 * Lane + node within lane (no `service`).
 * @param {string | undefined | null} workflow
 * @param {string | undefined | null} pipeline
 * @param {string | undefined | null} stage
 */
function factLaneNodeKey(workflow, pipeline, stage) {
	return `${trimPart(workflow)}\u0000${trimPart(pipeline)}\u0000${trimPart(stage)}`;
}

/**
 * Composite key for append-only facts and graph `factMatch` (lane + node within lane + producer).
 * @param {string | undefined | null} workflow
 * @param {string | undefined | null} pipeline
 * @param {string | undefined | null} stage
 * @param {string | undefined | null} service
 */
function factGraphBindKey(workflow, pipeline, stage, service) {
	return `${factLaneNodeKey(workflow, pipeline, stage)}\u0000${trimPart(service)}`;
}

/** @param {unknown} p */
function pipelineString(p) {
	if (p == null) return '';
	if (typeof p === 'number' && Number.isFinite(p)) return String(p);
	return String(p).trim();
}

/** @param {unknown} a @param {unknown} b */
function pipelineValuesEqual(a, b) {
	return pipelineString(a) === pipelineString(b);
}

/** @param {string | undefined} iso */
function factTimeMs(iso) {
	if (iso == null || String(iso).trim() === '') return null;
	const n = Date.parse(String(iso));
	return Number.isNaN(n) ? null : n;
}

/**
 * Primary timeline for recency: `occurredAt` from facts API (falls back to legacy `updatedAt` if present).
 * @param {import('./pipelineStatusTypes').PipelineStatusFact} f
 */
function primaryTimestamp(f) {
	if (f.occurredAt != null && String(f.occurredAt).trim() !== '') return String(f.occurredAt);
	const legacy = f.updatedAt;
	return legacy != null && String(legacy).trim() !== '' ? String(legacy) : undefined;
}

/**
 * Strict total ordering for picking the “latest” fact for one full bind key (`workflow`, `pipeline`, `stage`, `service`).
 * Primary: greater `occurredAt` wins.
 * Missing or unparseable time sorts before any valid timestamp.
 * Ties: lexicographic workflow+pipeline+stage+status, then id (lexicographic desc so stable and independent of input order).
 * @param {import('./pipelineStatusTypes').PipelineStatusFact} a
 * @param {import('./pipelineStatusTypes').PipelineStatusFact} b
 */
export function comparePipelineFactsRecency(a, b) {
	const ta = factTimeMs(primaryTimestamp(a));
	const tb = factTimeMs(primaryTimestamp(b));
	const va = ta === null ? Number.NEGATIVE_INFINITY : ta;
	const vb = tb === null ? Number.NEGATIVE_INFINITY : tb;
	if (va !== vb) return va - vb;
	const sa = `${trimPart(a.workflow)}\u0000${trimPart(a.pipeline)}\u0000${trimPart(a.stage)}\u0000${trimPart(a.status)}`;
	const sb = `${trimPart(b.workflow)}\u0000${trimPart(b.pipeline)}\u0000${trimPart(b.stage)}\u0000${trimPart(b.status)}`;
	if (sa !== sb) return sa < sb ? -1 : sa > sb ? 1 : 0;
	const ida = trimPart(a.id);
	const idb = trimPart(b.id);
	if (ida !== idb) return ida < idb ? 1 : ida > idb ? -1 : 0;
	return 0;
}

/** Normalized keys accepted from PortalApi `PipelineStatusFactDto.status` / SNS `PipelineStageStatus` names. */
const CANONICAL_BACKEND_STATUS_KEYS = new Set(['started', 'completed', 'failed', 'skipped']);

/** Dedupe dev warnings for the same unknown status string (normalized). */
const warnedUnknownBackendStatusKeys = new Set();

/**
 * Clears dedupe state for unknown-status `console.warn` (unit tests only).
 * @internal
 */
export function clearPipelineStatusUnknownStatusWarningsForTests() {
	warnedUnknownBackendStatusKeys.clear();
}

/**
 * @param {string} key normalized with {@link norm}
 * @param {string | undefined | null} raw original status for logging
 */
function warnNonCanonicalBackendStatus(raw, key) {
	if (process.env.NODE_ENV === 'production') return;
	if (!key || CANONICAL_BACKEND_STATUS_KEYS.has(key)) return;
	if (warnedUnknownBackendStatusKeys.has(key)) return;
	warnedUnknownBackendStatusKeys.add(key);
	// eslint-disable-next-line no-console -- intentional fail-soft diagnostics for contract drift
	console.warn(
		`[pipelineStatus] Non-canonical backend status ${JSON.stringify(raw ?? '')}; expected Started, Completed, Failed, or Skipped (case-insensitive). Mapping UI state to pending.`,
	);
}

/**
 * Single semantic map: persisted / DTO `status` strings → UI `PipelineStatusState`.
 *
 * **Authoritative contract:** PortalApi / SNS `PipelineStageStatus` enum names `Started`, `Completed`, `Failed`, `Skipped`
 * (trimmed; compared case-insensitively). Anything else maps to `pending` and logs a one-time dev warning per distinct value.
 *
 * @param {string | undefined | null} status
 * @returns {import('./pipelineStatusTypes').PipelineStatusState}
 */
export function mapBackendPipelineStatusToPipelineStatusState(status) {
	const key = norm(status);
	if (!key) return 'pending';
	if (key === 'started') return 'running';
	if (key === 'completed') return 'success';
	if (key === 'failed') return 'failed';
	if (key === 'skipped') return 'skipped';
	warnNonCanonicalBackendStatus(status, key);
	return 'pending';
}

/**
 * Latest fact per `(workflow, pipeline, stage, service)` using `comparePipelineFactsRecency` (deterministic).
 * Rows missing any of the four fields (after trim) are skipped.
 * @param {import('./pipelineStatusTypes').PipelineStatusFact[]} facts
 * @returns {Map<string, import('./pipelineStatusTypes').PipelineStatusFact>}
 */
function indexLatestFactsByGraphBindKey(facts) {
	/** @type {Map<string, import('./pipelineStatusTypes').PipelineStatusFact>} */
	const map = new Map();
	for (const f of facts) {
		const svc = trimPart(f.service);
		if (!trimPart(f.workflow) || !trimPart(f.pipeline) || !trimPart(f.stage) || !svc) continue;
		const k = factGraphBindKey(f.workflow, f.pipeline, f.stage, svc);
		const prev = map.get(k);
		if (!prev) {
			map.set(k, f);
			continue;
		}
		if (comparePipelineFactsRecency(f, prev) > 0) map.set(k, f);
	}
	return map;
}

/**
 * All bindable facts grouped by graph bind key (for Started → terminal duration).
 * @param {import('./pipelineStatusTypes').PipelineStatusFact[]} facts
 * @returns {Map<string, import('./pipelineStatusTypes').PipelineStatusFact[]>}
 */
function indexAllFactsByGraphBindKey(facts) {
	/** @type {Map<string, import('./pipelineStatusTypes').PipelineStatusFact[]>} */
	const map = new Map();
	for (const f of facts) {
		const svc = trimPart(f.service);
		if (!trimPart(f.workflow) || !trimPart(f.pipeline) || !trimPart(f.stage) || !svc) continue;
		const k = factGraphBindKey(f.workflow, f.pipeline, f.stage, svc);
		const list = map.get(k);
		if (list) list.push(f);
		else map.set(k, [f]);
	}
	return map;
}

/**
 * Started / terminal timestamps for one bind key from the fact history.
 * @param {import('./pipelineStatusTypes').PipelineStatusFact[] | undefined} rows
 * @returns {{ startedAt?: string, completedAt?: string }}
 */
function timingFromFactRows(rows) {
	if (!Array.isArray(rows) || rows.length === 0) return {};
	const startedRows = rows.filter((f) => norm(f.status) === 'started');
	let startedAt;
	if (startedRows.length) {
		const earliest = startedRows.reduce((best, cur) =>
			(factTimeMs(primaryTimestamp(cur)) ?? Number.POSITIVE_INFINITY)
			< (factTimeMs(primaryTimestamp(best)) ?? Number.POSITIVE_INFINITY)
				? cur
				: best,
		);
		startedAt = primaryTimestamp(earliest);
	}
	const terminalRows = rows.filter((f) => {
		const s = norm(f.status);
		return s === 'completed' || s === 'failed' || s === 'skipped';
	});
	let completedAt;
	if (terminalRows.length) {
		const latest = terminalRows.reduce((best, cur) =>
			comparePipelineFactsRecency(cur, best) > 0 ? cur : best,
		);
		completedAt = primaryTimestamp(latest);
	}
	return { startedAt, completedAt };
}

/**
 * Earliest instance timestamp strictly before the latest completion.
 * Used when a multi-instance node has no Started fact, so the card can show the span
 * across instances instead of a blank duration. A single timestamp is not a start.
 * @param {import('./pipelineStatusTypes').PipelineStatusFact[]} rows
 * @param {string | undefined} completedAt
 */
function earliestInstanceTimeBefore(rows, completedAt) {
	const end = factTimeMs(completedAt);
	if (end == null) return undefined;
	let bestMs = /** @type {number | null} */ (null);
	let bestIso;
	for (const row of rows) {
		const iso = primaryTimestamp(row);
		const t = factTimeMs(iso);
		if (t == null || t >= end) continue;
		if (bestMs == null || t < bestMs) {
			bestMs = t;
			bestIso = iso;
		}
	}
	return bestIso;
}

/**
 * @param {import('./pipelineStatusTypes').PipelineStatusGraphDefinition} definition
 * @param {string} parentStageName
 */
function findParentGraphStage(definition, parentStageName) {
	const want = trimPart(parentStageName);
	return definition.stages.find((s) => trimPart(s.factMatch?.stage) === want);
}

/**
 * @param {import('./pipelineStatusTypes').PipelineStatusIterationManifest} m
 * @param {string} parentCorrelationId
 * @param {import('./pipelineStatusTypes').PipelineStatusFactMatch} factMatch
 * @param {import('./pipelineStatusTypes').PipelineStatusIterationManifestMatch} imm
 * @param {string | undefined} projectId
 */
function manifestMatchesStage(
	m,
	parentCorrelationId,
	factMatch,
	imm,
	projectId,
) {
	if (trimPart(m.correlationId) !== trimPart(parentCorrelationId)) return false;
	if (projectId && trimPart(m.subjectId) !== trimPart(projectId)) return false;
	if (trimPart(m.workflow) !== trimPart(factMatch.workflow)) return false;
	if (!pipelineValuesEqual(m.pipeline, factMatch.pipeline)) return false;
	if (trimPart(m.parentStage) !== trimPart(imm.parentStage)) return false;
	if (trimPart(m.iterationDimension) !== trimPart(imm.iterationDimension)) return false;
	if (trimPart(imm.parentService || '')) {
		if (trimPart(m.parentService || '') !== trimPart(imm.parentService)) return false;
	}
	return true;
}

/** @param {string | undefined} iso */
function createdAtUtcMs(iso) {
	const t = factTimeMs(iso);
	return t === null ? Number.NEGATIVE_INFINITY : t;
}

/**
 * @param {import('./pipelineStatusTypes').PipelineStatusFactMatch} factMatch
 * @param {string} correlationId
 * @param {string} iterationDimension
 * @param {string} iterationValue
 * @param {import('./pipelineStatusTypes').PipelineStatusFact[]} facts
 */
function latestFactForIterationBind(factMatch, correlationId, iterationDimension, iterationValue, facts) {
	const c = trimPart(correlationId);
	const dim = trimPart(iterationDimension);
	const val = trimPart(iterationValue);
	/** @type {import('./pipelineStatusTypes').PipelineStatusFact[]} */
	const candidates = [];
	for (const f of facts) {
		const svc = trimPart(f.service);
		if (!trimPart(f.workflow) || !trimPart(f.pipeline) || !trimPart(f.stage) || !svc) continue;
		if (trimPart(f.correlationId) !== c) continue;
		if (trimPart(f.workflow) !== trimPart(factMatch.workflow)) continue;
		if (!pipelineValuesEqual(f.pipeline, factMatch.pipeline)) continue;
		if (trimPart(f.stage) !== trimPart(factMatch.stage)) continue;
		if (trimPart(f.service) !== trimPart(factMatch.service)) continue;
		if (trimPart(f.iterationDimension) !== dim) continue;
		if (trimPart(f.iterationValue) !== val) continue;
		candidates.push(f);
	}
	if (!candidates.length) return undefined;
	return candidates.reduce((best, cur) => (comparePipelineFactsRecency(cur, best) > 0 ? cur : best));
}

/**
 * @param {import('./pipelineStatusTypes').PipelineStatusGraphStage} defStage
 * @param {Omit<import('./pipelineStatusTypes').PipelineStatusGraphStage, 'factMatch' | 'iterationManifestMatch' | 'completionPolicy'>} rest
 * @param {import('./pipelineStatusTypes').PipelineStatusFactMatch} factMatch
 * @param {import('./pipelineStatusTypes').PipelineStatusIterationManifestMatch} imm
 * @param {import('./pipelineStatusTypes').PipelineStatusGraphDefinition} definition
 * @param {import('./pipelineStatusTypes').PipelineStatusFact[]} facts
 * @param {import('./pipelineStatusTypes').PipelineStatusIterationManifest[]} iterationManifests
 * @param {Map<string, import('./pipelineStatusTypes').PipelineStatusFact>} latestByKey
 * @param {string | undefined} projectId
 */
function buildManifestBackedStageViewModel(
	defStage,
	rest,
	factMatch,
	imm,
	definition,
	facts,
	iterationManifests,
	latestByKey,
	projectId,
) {
	const scopeFromDef = trimPart(factMatch.scope);
	const workflowFromDef = trimPart(factMatch.workflow);
	const displayService = trimPart(rest.service);

	const parentDef = findParentGraphStage(definition, imm.parentStage);
	if (!parentDef?.factMatch) {
		throw new Error(
			`[pipelineStatus] Stage ${JSON.stringify(defStage.id)}: iterationManifestMatch.parentStage ${JSON.stringify(imm.parentStage)} does not match any graph stage factMatch.stage.`,
		);
	}
	const parentKey = factGraphBindKey(
		parentDef.factMatch.workflow,
		parentDef.factMatch.pipeline,
		parentDef.factMatch.stage,
		parentDef.factMatch.service,
	);
	const parentFact = latestByKey.get(parentKey);
	const parentCorrelationId = trimPart(parentFact?.correlationId);
	if (!parentCorrelationId) {
		return {
			...rest,
			status: /** @type {import('./pipelineStatusTypes').PipelineStatusState} */ ('pending'),
			occurredAt: undefined,
			factId: undefined,
			scope: scopeFromDef,
			workflow: workflowFromDef,
			subjectId: undefined,
			correlationId: undefined,
			service: displayService,
			serviceVersion: undefined,
		};
	}

	const matches = iterationManifests.filter((m) =>
		manifestMatchesStage(m, parentCorrelationId, factMatch, imm, projectId),
	);
	if (!matches.length) {
		return {
			...rest,
			status: /** @type {import('./pipelineStatusTypes').PipelineStatusState} */ ('pending'),
			occurredAt: undefined,
			factId: undefined,
			scope: scopeFromDef,
			workflow: workflowFromDef,
			subjectId: undefined,
			correlationId: parentCorrelationId,
			service: displayService,
			serviceVersion: undefined,
		};
	}

	const manifest = [...matches].sort((a, b) => createdAtUtcMs(b.createdAtUtc) - createdAtUtcMs(a.createdAtUtc))[0];
	const expected = Array.isArray(manifest.iterationValues)
		? manifest.iterationValues.map((v) => trimPart(v)).filter(Boolean)
		: [];
	const iterationTotalCount = expected.length;
	if (!expected.length) {
		return {
			...rest,
			status: /** @type {import('./pipelineStatusTypes').PipelineStatusState} */ ('pending'),
			occurredAt: undefined,
			factId: undefined,
			scope: scopeFromDef,
			workflow: workflowFromDef,
			subjectId: trimPart(manifest.subjectId) || undefined,
			correlationId: trimPart(manifest.correlationId),
			service: displayService,
			serviceVersion: undefined,
			iterationCompletedCount: 0,
			iterationTotalCount: 0,
		};
	}

	/** @type {import('./pipelineStatusTypes').PipelineStatusFact[]} */
	const perIteration = [];
	let anyFailed = false;
	let iterationCompletedCount = 0;
	for (const ev of expected) {
		const lf = latestFactForIterationBind(factMatch, manifest.correlationId, manifest.iterationDimension, ev, facts);
		if (!lf) {
			continue;
		}
		perIteration.push(lf);
		const ns = norm(lf.status);
		if (ns === 'failed') anyFailed = true;
		if (ns === 'completed' || ns === 'skipped') iterationCompletedCount += 1;
	}

	const incomplete = iterationCompletedCount < iterationTotalCount;
	let status = /** @type {import('./pipelineStatusTypes').PipelineStatusState} */ ('running');
	if (anyFailed) status = 'failed';
	else if (!incomplete) status = 'success';
	else if (perIteration.length === 0) status = 'pending';

	const aggregate =
		perIteration.length > 0
			? perIteration.reduce((best, cur) => (comparePipelineFactsRecency(cur, best) > 0 ? cur : best))
			: undefined;

	/** @type {import('./pipelineStatusTypes').PipelineStatusFact[]} */
	const timingRows = [];
	for (const ev of expected) {
		const c = trimPart(manifest.correlationId);
		const dim = trimPart(manifest.iterationDimension);
		const val = trimPart(ev);
		for (const f of facts) {
			const svc = trimPart(f.service);
			if (!trimPart(f.workflow) || !trimPart(f.pipeline) || !trimPart(f.stage) || !svc) continue;
			if (trimPart(f.correlationId) !== c) continue;
			if (trimPart(f.workflow) !== trimPart(factMatch.workflow)) continue;
			if (!pipelineValuesEqual(f.pipeline, factMatch.pipeline)) continue;
			if (trimPart(f.stage) !== trimPart(factMatch.stage)) continue;
			if (trimPart(f.service) !== trimPart(factMatch.service)) continue;
			if (trimPart(f.iterationDimension) !== dim) continue;
			if (trimPart(f.iterationValue) !== val) continue;
			timingRows.push(f);
		}
	}
	const timing = timingFromFactRows(timingRows);
	const startedAt = timing.startedAt
		?? (iterationTotalCount > 1 ? earliestInstanceTimeBefore(timingRows, timing.completedAt) : undefined);

	return {
		...rest,
		status,
		occurredAt: aggregate ? primaryTimestamp(aggregate) : undefined,
		factId: aggregate?.id,
		scope: scopeFromDef,
		workflow: workflowFromDef,
		subjectId: (aggregate?.subjectId ?? trimPart(manifest.subjectId)) || undefined,
		correlationId: trimPart(manifest.correlationId),
		service: displayService,
		serviceVersion: aggregate?.serviceVersion ?? undefined,
		iterationCompletedCount,
		iterationTotalCount,
		startedAt,
		completedAt: status === 'success' || status === 'failed' ? timing.completedAt : undefined,
	};
}

/**
 * Pure: one `PipelineStatusStage` per definition stage, stable ids, preserved `dependsOn`.
 * Facts bind only through each stage's `factMatch` (not by `description`).
 * @param {import('./pipelineStatusTypes').PipelineStatusGraphDefinition} definition
 * @param {import('./pipelineStatusTypes').PipelineStatusFact[]} facts
 * @param {import('./pipelineStatusTypes').PipelineStatusIterationManifest[]} [iterationManifests]
 * @param {string | undefined} [projectId]
 * @returns {import('./pipelineStatusTypes').PipelineStatusStage[]}
 */
export function buildPipelineStatusGraphViewModel(definition, facts, iterationManifests, projectId) {
	const manifests = Array.isArray(iterationManifests) ? iterationManifests : [];
	const latestByKey = indexLatestFactsByGraphBindKey(facts);
	const allByKey = indexAllFactsByGraphBindKey(facts);

	const stages = definition.stages.map((defStage) => {
		const { factMatch, iterationManifestMatch, completionPolicy, ...rest } = defStage;
		if (!trimPart(factMatch.service)) {
			throw new Error(
				`[pipelineStatus] Stage ${JSON.stringify(defStage.id)}: factMatch.service is required and must be non-empty after trim.`,
			);
		}
		const hasImm = iterationManifestMatch != null && typeof iterationManifestMatch === 'object';
		const hasPolicy = completionPolicy != null && String(completionPolicy).trim() !== '';
		if (hasImm !== hasPolicy) {
			throw new Error(
				`[pipelineStatus] Stage ${JSON.stringify(defStage.id)}: iterationManifestMatch and completionPolicy must both be set or both be omitted.`,
			);
		}
		if (hasPolicy && completionPolicy !== 'allIterationsSucceeded') {
			throw new Error(
				`[pipelineStatus] Stage ${JSON.stringify(defStage.id)}: unsupported completionPolicy ${JSON.stringify(completionPolicy)} (expected allIterationsSucceeded).`,
			);
		}

		if (hasImm && completionPolicy === 'allIterationsSucceeded') {
			const scopeFromDef = trimPart(factMatch.scope);
			if (!scopeFromDef) {
				throw new Error(
					`[pipelineStatus] Stage ${JSON.stringify(defStage.id)}: factMatch.scope is required and must be non-empty after trim.`,
				);
			}
			const workflowFromDef = trimPart(factMatch.workflow);
			if (!workflowFromDef) {
				throw new Error(
					`[pipelineStatus] Stage ${JSON.stringify(defStage.id)}: factMatch.workflow is required and must be non-empty after trim.`,
				);
			}
			const displayService = trimPart(rest.service);
			if (!displayService) {
				throw new Error(
					`[pipelineStatus] Stage ${JSON.stringify(defStage.id)}: top-level service is required for display and must be non-empty after trim.`,
				);
			}
			return buildManifestBackedStageViewModel(
				defStage,
				rest,
				factMatch,
				/** @type {import('./pipelineStatusTypes').PipelineStatusIterationManifestMatch} */ (iterationManifestMatch),
				definition,
				facts,
				manifests,
				latestByKey,
				projectId,
			);
		}

		const k = factGraphBindKey(
			factMatch.workflow,
			factMatch.pipeline,
			factMatch.stage,
			factMatch.service,
		);
		const fact = latestByKey.get(k);
		const raw = fact?.status;
		const status = mapBackendPipelineStatusToPipelineStatusState(raw);
		const ts = fact ? primaryTimestamp(fact) : undefined;
		const timing = timingFromFactRows(allByKey.get(k));

		const scopeFromDef = trimPart(factMatch.scope);
		if (!scopeFromDef) {
			throw new Error(
				`[pipelineStatus] Stage ${JSON.stringify(defStage.id)}: factMatch.scope is required and must be non-empty after trim.`,
			);
		}
		const workflowFromDef = trimPart(factMatch.workflow);
		if (!workflowFromDef) {
			throw new Error(
				`[pipelineStatus] Stage ${JSON.stringify(defStage.id)}: factMatch.workflow is required and must be non-empty after trim.`,
			);
		}
		const displayService = trimPart(rest.service);
		if (!displayService) {
			throw new Error(
				`[pipelineStatus] Stage ${JSON.stringify(defStage.id)}: top-level service is required for display and must be non-empty after trim.`,
			);
		}

		return {
			...rest,
			status,
			occurredAt: ts,
			factId: fact?.id,
			scope: scopeFromDef,
			workflow: workflowFromDef,
			subjectId: fact?.subjectId,
			correlationId: fact?.correlationId ?? undefined,
			service: displayService,
			serviceVersion: fact?.serviceVersion ?? undefined,
			startedAt: timing.startedAt,
			completedAt: status === 'success' || status === 'failed' || status === 'skipped' ? timing.completedAt : undefined,
		};
	});
	if (stages.length !== definition.stages.length) {
		throw new Error('PipelineStatus graph view-model must emit one stage per graph definition stage.');
	}
	return omitPathIllegalStarts(stages);
}

